<script lang="ts">
import { templateRef, useElementSize } from '@vueuse/core'
import QRCode from 'easyqrcodejs'
import { defineComponent, nextTick, onMounted, ref, watch } from 'vue'

import { useShortenUrl } from '@/composables/useShortenUrl'
import { useStoreModalState } from '@/composables/useStoreModalState'
import { getAssetURL } from '@/utils'
import { useStoreSnapshot } from '@/webgl/composables/useStoreSnapshot'

import CtaComponent from '../atoms/CtaComponent.vue'

export default defineComponent({
  name: 'ModalScanQR',
  components: {
    CtaComponent
  },
  props: {
  },
  emits: [],
  setup (props, { emit }) {
    const qrcodeEl = templateRef<HTMLDivElement>('qrcodeEl')
    const qr = ref()

    const { snapshotQR } = useStoreSnapshot()
    const { toggleModal } = useStoreModalState()
    const { width: qrWidth, height: qrHeight } = useElementSize(qrcodeEl)

    const qrOptions = ref({
      correctLevel: QRCode.CorrectLevel.Q,
      width: qrWidth.value,
      height: qrHeight.value,
      quietZone: 0,
      logo: getAssetURL('textures/watermark_qr.png'),
      logoWidth: 80,
      logoHeight: 80,
      drawer: 'canvas'
    })

    const restartExperience = () => { window.location.reload() }

    const drawQR = () => {
      qrOptions.value.height = qrWidth.value
      qrOptions.value.height = qrHeight.value

      const code = new QRCode(qrcodeEl.value, { ...qrOptions.value, text: snapshotQR.value })
      qr.value = code
    }

    watch([qr, qrWidth, qrHeight], ([_qr, _w, _h]) => {
      if (!_qr || !_w || !_h) return

      qr.value.resize(_w, _h)
    }, { immediate: true })

    onMounted(async () => {
      await nextTick()
      if (snapshotQR.value) {
        const { shortUrl } = useShortenUrl(snapshotQR.value)
        console.log(shortUrl)
        drawQR()
      }
    })

    return {
      getAssetURL,
      toggleModal,
      restartExperience
    }
  }
})
</script>

<template>
  <div class="modal-wrapper absolute top-0 left-0 h-lview w-full">
    <div class="focus absolute top-0 left-0 w-full h-full cursor-pointer" @click="toggleModal(false)" />
    <div class="modal-ctn absolute w-full flex flex-col items-center justify-center bottom-0 left-0 py-16 <sm:py-10 <xs:py-6 px-8 <sm:px-4 <xs:px-2">
      <h2 class="h2 uppercase text-black max-w-100 text-center mb-4">Scan to receive a picture</h2>
      <img
        class="qr-code hidden aspect-1 <xs:max-w-[60%] max-w-[40%] max-h-[40%]"
        :src="getAssetURL('textures/qr_test.png')"
        alt="qr code test"
      />
      <div ref="qrcodeEl" class="qr-code aspect-1 rounded-xl h-50 xs:h-60 sm:h-100 max-h-100 w-50 xs:w-60 sm:w-100 max-w-100 p-4 bg-white"></div>
      <div class="wrapper-footer absolute bottom-6 flex flex-col gap-0 items-center justify-end">
        <CtaComponent class="border-none underline p-0" @click="restartExperience()">Restart the Experience</CtaComponent>
        <p class="body-2 text-center">Dolce&Gabbana Beauty commits to delete your image within 7 days</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.modal-wrapper {
  z-index: $z-modal;
  background-color: rgba(0, 0, 0, 43.7%);
}

.focus {
  z-index: 16;
}

.modal-ctn {
  z-index: 17;
  height: calc(100% - ((8px * 2) + 80px));
  border: 2px solid $c-white;
  border-radius: 20px 20px 0 0;
  backdrop-filter: $backdrop-modal;
  background: rgb(255,255,255);
  background: $c-card-gradient;
  background-color: rgba(237, 242, 245, 30%);
  box-shadow: inset 3px 3px 4px #eaeaeace, inset -3px -1px 11px #EAEAEA;

@screen sm {
  height: calc(100% - ((16px * 2) + 80px));
}
}
</style>
